import { bls12_381 as bls } from '@noble/curves/bls12-381.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { bytesToHex, hexToBytes, utf8ToBytes, concatBytes } from '@noble/hashes/utils.js';
import { isHex, digestHex, signHex, publicKeyOf } from './crypto.js';

/**
 * Shield notes: bearer notes of a fixed denomination, blind-signed by the
 * sequencer (Chaumian e-cash over BLS12-381, signatures in G1, keys in G2).
 *
 *   wallet:    B = r·H(denom, serial)              → note_mint (pays denom)
 *   sequencer: S' = sk·B                            → in the entry result
 *   wallet:    S = r⁻¹·S'   (valid BLS sig on H, never seen by the sequencer)
 *   anyone:    e(S, G2) = e(H(denom, serial), pk)  → note_redeem (credits denom)
 *
 * Blinding is information-theoretic, so not even the sequencer can link a
 * redemption to the mint it came from. Every check is public (pairings
 * against the keys in genesis), so replay verifies all of it.
 */
type G1 = InstanceType<typeof bls.G1.Point>;
type G2 = InstanceType<typeof bls.G2.Point>;
const Fr = bls.fields.Fr;
const G1P = bls.G1.Point;
const G2P = bls.G2.Point;

/** Denominations in zat: 0.01, 0.1, 1 and 10 ZEC. */
export const NOTE_DENOMS = ['1000000', '10000000', '100000000', '1000000000'] as const;
export const MAX_NOTES_PER_REQUEST = 20;

const dst = utf8ToBytes('DAZZLE/v1/note\n');

/** The point a note's signature is on: depends on denomination and serial. */
export function noteMessagePoint(denom: string, serialHex: string): G1 {
  return bls.shortSignatures.hash(concatBytes(dst, utf8ToBytes(denom + '\n'), hexToBytes(serialHex))) as G1;
}

export function parseG1(hex: unknown): G1 | null {
  if (!isHex(hex, 48)) return null;
  try {
    const p = G1P.fromHex(hex);
    p.assertValidity();
    return p.equals(G1P.ZERO) ? null : p;
  } catch {
    return null;
  }
}

export function parseG2(hex: unknown): G2 | null {
  if (!isHex(hex, 96)) return null;
  try {
    const p = G2P.fromHex(hex);
    p.assertValidity();
    return p.equals(G2P.ZERO) ? null : p;
  } catch {
    return null;
  }
}

const g1Hex = (p: G1) => p.toHex(true);

/** Nonzero scalar from key material (reduced mod r). */
function scalarFrom(bytes: Uint8Array): bigint {
  let x = 0n;
  for (const b of bytes) x = (x << 8n) | BigInt(b);
  const s = x % Fr.ORDER;
  return s === 0n ? 1n : s;
}

// ------------------------------------------------------------ sequencer side

export function noteSecretKey(sequencerSecretHex: string, denom: string): bigint {
  return scalarFrom(hkdf(sha256, hexToBytes(sequencerSecretHex), undefined, utf8ToBytes(`DAZZLE/v1/note-key/${denom}`), 48));
}

export function notePublicKey(sk: bigint): string {
  return G2P.BASE.multiply(sk).toHex(true);
}

/** Sign blinded points; null if any of them is not a valid G1 point. */
export function blindSign(sk: bigint, blinded: string[]): string[] | null {
  const out: string[] = [];
  for (const b of blinded) {
    const p = parseG1(b);
    if (!p) return null;
    out.push(g1Hex(p.multiply(sk)));
  }
  return out;
}

// ------------------------------------------------------------ verification

/**
 * Check Σ cᵢ·sigᵢ against Σ cᵢ·msgᵢ with one pairing equation:
 * e(Σ cᵢ sigᵢ, G2) = e(Σ cᵢ msgᵢ, pk). The coefficients come from a hash of
 * every input, so a single bad signature cannot be cancelled out by others.
 */
function batchCheck(pk: G2, msgs: G1[], sigs: G1[]): boolean {
  if (msgs.length === 0 || msgs.length !== sigs.length) return false;
  const seed = sha256(concatBytes(utf8ToBytes('DAZZLE/v1/batch\n'), ...msgs.map((m) => m.toBytes(true)), ...sigs.map((s) => s.toBytes(true))));
  let accM = G1P.ZERO;
  let accS = G1P.ZERO;
  for (let i = 0; i < msgs.length; i++) {
    const c = scalarFrom(sha256(concatBytes(seed, utf8ToBytes(String(i)))).subarray(0, 16));
    accM = accM.add(msgs[i].multiply(c));
    accS = accS.add(sigs[i].multiply(c));
  }
  if (accM.equals(G1P.ZERO) || accS.equals(G1P.ZERO)) return false;
  const gt = bls.pairingBatch([
    { g1: accS, g2: G2P.BASE },
    { g1: accM.negate(), g2: pk },
  ]);
  return bls.fields.Fp12.eql(gt, bls.fields.Fp12.ONE);
}

/** The sequencer's blind signatures match the blinded points it was paid for. */
export function verifyBlindSigs(pk: G2, blinded: string[], sigs: string[]): boolean {
  const m = blinded.map(parseG1);
  const s = sigs.map(parseG1);
  if (m.some((x) => !x) || s.some((x) => !x)) return false;
  return batchCheck(pk, m as G1[], s as G1[]);
}

/** Unblinded notes carry valid signatures for this denomination. */
export function verifyNotes(pk: G2, denom: string, notes: { serial: string; sig: string }[]): boolean {
  const s = notes.map((n) => parseG1(n.sig));
  if (s.some((x) => !x)) return false;
  return batchCheck(pk, notes.map((n) => noteMessagePoint(denom, n.serial)), s as G1[]);
}

// ------------------------------------------------------------ wallet side

/**
 * The wallet's k-th note of a denomination, all derived from its seed so it
 * can be recovered. The serial is an ed25519 public key: redeeming needs a
 * signature by its secret (noteAuth), so whoever sees a note in flight cannot
 * redeem it into another account. Serials differ per denomination, so the
 * same counter used for two denominations never yields two notes with one
 * serial.
 */
export function noteSecrets(seedHex: string, k: number, denom: string): { secret: string; serial: string; r: bigint } {
  const seed = hexToBytes(seedHex);
  const secret = bytesToHex(hkdf(sha256, seed, undefined, utf8ToBytes(`DAZZLE/v3/note-key/${denom}/${k}`), 32));
  const serial = publicKeyOf(secret);
  const r = scalarFrom(hkdf(sha256, seed, undefined, utf8ToBytes(`DAZZLE/v3/note-blind/${denom}/${k}`), 48));
  return { secret, serial, r };
}

/** What a note's key signs to be redeemed into `to` on log `chain`. */
export function noteAuthMessage(chain: string, denom: string, serial: string, to: string): string {
  return digestHex('note_redeem', { chain, denom, serial, to });
}

export function noteAuth(secret: string, chain: string, denom: string, serial: string, to: string): string {
  return signHex(noteAuthMessage(chain, denom, serial, to), secret);
}

export function blind(denom: string, serialHex: string, r: bigint): string {
  return g1Hex(noteMessagePoint(denom, serialHex).multiply(r));
}

export function unblind(blindSigHex: string, r: bigint): string {
  const p = parseG1(blindSigHex);
  if (!p) throw new Error('bad blind signature');
  return g1Hex(p.multiply(Fr.inv(r)));
}
